import { Box, Divider, ListSubheader, Menu, MenuItem, Typography } from "@mui/material"
import { KeyboardArrowDownOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useEffect, useMemo, useRef, useState } from "react"

import { ProductVersionChip } from "./ProductVersionChip"

import type { ProductStockType, ProductVersionInfo } from "@/graphql/codegen/graphql"
import { ProductVersionStatus } from "@/graphql/codegen/graphql"
import { PRODUCT_STOCK_TYPE_COLOR_MAP, PRODUCT_STOCK_TYPE_LABEL_MAP } from "@/screens/Products/constants"
import { groupByStatus } from "@/screens/Products/utils/productVersion"
import { formatDate } from "@/utils/date"

interface ProductVersionMenuProps {
  productVersionId?: number
  productVersions: ProductVersionInfo[]
  displayDraft?: boolean
  onSelectProductVersion: (versionInfo: ProductVersionInfo) => void
}

const ProductVersionMenu: FC<ProductVersionMenuProps> = ({
  productVersionId,
  productVersions,
  displayDraft,
  onSelectProductVersion,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)

  const [currentVersion, setCurrentVersion] = useState<ProductVersionInfo | undefined>(
    productVersions.find((el) => el.id === productVersionId)
  )

  const anchorElRef = useRef<HTMLDivElement>(null)

  const groupedVersions = useMemo(
    () => Object.fromEntries(groupByStatus(productVersions, !displayDraft)),
    [displayDraft, productVersions]
  )

  const isMultiple = Object.values(groupedVersions).flat().length > 1

  const handleToggleProductVersion = () => {
    setIsOpen(!isOpen)
  }

  const handleSelectVersion = (versionInfo: ProductVersionInfo) => {
    handleToggleProductVersion()
    onSelectProductVersion(versionInfo)
  }

  useEffect(() => {
    setCurrentVersion(productVersions.find((el) => el.id === productVersionId))
  }, [productVersionId, productVersions])

  const renderVersionMenuItem = (item: ProductVersionInfo) => {
    return (
      <MenuItem key={item.id} onClick={() => handleSelectVersion(item)} selected={item.id === currentVersion?.id}>
        <Box className='flex w-full items-center justify-between'>
          <Box>
            <Typography variant='body2'>
              v{item.versionNumber} {formatDate(item.releaseDate)}
            </Typography>
          </Box>
          <Typography variant='body2' color={PRODUCT_STOCK_TYPE_COLOR_MAP.get(item.stockType as ProductStockType)}>
            {PRODUCT_STOCK_TYPE_LABEL_MAP[item.stockType as ProductStockType]}
          </Typography>
        </Box>
      </MenuItem>
    )
  }

  return (
    <>
      <ProductVersionChip
        version={currentVersion}
        isInteractive={isMultiple}
        deleteIcon={<KeyboardArrowDownOutlined className='text-gray-800' />}
        isTooltipVisible={!isMultiple}
        tooltipTitle='This product has only one version.'
        anchorRef={anchorElRef}
        onClick={handleToggleProductVersion}
        onDelete={handleToggleProductVersion}
      />

      <Menu
        anchorEl={anchorElRef.current}
        open={isOpen}
        onClose={handleToggleProductVersion}
        slotProps={{
          paper: {
            sx: {
              minWidth: 260,
            },
          },
        }}
      >
        {/* Current */}
        {groupedVersions[ProductVersionStatus.Active]?.length && (
          <>
            <ListSubheader className='leading-9'>Current</ListSubheader>
            <Divider />
            {groupedVersions[ProductVersionStatus.Active].map((v) => renderVersionMenuItem(v))}
            <Divider />
          </>
        )}

        {/* Draft */}
        {displayDraft && groupedVersions[ProductVersionStatus.Draft]?.length && (
          <>
            <ListSubheader className='leading-9'>Draft</ListSubheader>
            <Divider />
            {groupedVersions[ProductVersionStatus.Draft].map((v) => renderVersionMenuItem(v))}
          </>
        )}

        {/* Past */}
        {groupedVersions[ProductVersionStatus.Past]?.length > 0 && (
          <>
            <ListSubheader className='leading-9'>Past</ListSubheader>
            <Divider />
            {groupedVersions[ProductVersionStatus.Past].map((v) => renderVersionMenuItem(v))}
          </>
        )}
      </Menu>
    </>
  )
}

export default ProductVersionMenu
